define('viewModels/privileged/callbacks/ScheduledCallbacks',[
    'lib/bluebird',
    'lib/knockout',
    'lib/lodash4',
    'moment',
    'services/apiCore',
    'services/authService',
    'services/divisionService',
    'services/featureToggleService',
    'services/onlineService',
    'services/queuesService',
    'viewModels/widgets/Pager',
    'viewModels/privileged/callbacks/ScheduledCallbackRow',
    'viewModels/ViewModel'
], function (
    Promise,
    ko,
    _,
    moment,
    apiCore,
    authService,
    divisionService,
    featureToggleService,
    onlineService,
    queuesService,
    Pager,
    ScheduledCallbackRow,
    ViewModel
) {
    'use strict';

    var last14Days = function () {
        var end = moment();
        var start = end.clone().subtract(14, 'days');
        return start.toISOString() + "/" + end.toISOString();
    };

    var getSegmentFilters = function (queue, campaign) {
        var predicates = [
            {
                "dimension": "mediaType",
                "value": "CALLBACK"
            },
            {
                "dimension": "segmentType",
                "value": "Scheduled"
            },
            {
                "dimension": "segmentEnd",
                "operator": "notExists"
            }
        ];

        if (queue && queue.id) {
            predicates.push({
                "dimension": "queueId",
                "value": queue.id
            });
        }

        var segmentFilters = [
            {
                "type": "and",
                "predicates": predicates
            }
        ];

        if (campaign && campaign.id) {
            segmentFilters.push({
                "type": "and",
                "predicates": [
                    {
                        "dimension": "outboundCampaignId",
                        "value": campaign.id
                    }
                ]
            });
        }

        return segmentFilters;
    };

    var getConversationFilters = function (division) {
        if (division && division.id) {
            return [
                {
                    "type": "and",
                    "predicates": [
                        {
                            "dimension": "divisionId",
                            "value": division.id
                        }
                    ]
                }
            ];
        } else {
            return [];
        }
    };

    return ViewModel.extend(function () {
        var self = this;

        var divisionsEnabled = featureToggleService.getFeatureToggle('authorization.division.management')();

        // ====================================================================
        // Grid setup
        // ====================================================================

        self.isLoading = ko.observable(true);
        self.rows = ko.observableArray([]);

        self.pager = self.subView(Pager);
        self.pager.hasPageCount(false);
        self.pageOptions = self.computed(function () {
            return {
                pageNumber: self.pager.pageNumber(),
                pageSize: self.pager.pageSize()
            };
        });

        self.columns = [
            {
                id: 'callbackUserName',
                label: 'scheduledCallbacks.callbackUserName'
            },
            {
                id: 'callbackNumbers',
                label: 'scheduledCallbacks.callbackNumbers'
            },
            {
                id: 'callbackScheduledTime',
                label: 'scheduledCallbacks.callbackScheduledTime'
            },
            {
                id: 'queueName',
                label: 'scheduledCallbacks.queueName'
            },
            {
                id: 'campaignName',
                label: 'scheduledCallbacks.campaignName'
            },
            {
                id: 'divisions',
                label: 'scheduledCallbacks.divisions'
            },
            {
                id: 'actions',
                label: 'scheduledCallbacks.actions'
            }
        ];

        self.visibleColumnIds = authService.hasAuthorization('perm:outbound:campaign:view', divisionsEnabled) ?
            _.map(self.columns, 'id') :
            _.chain(self.columns).map('id').without('campaignName').value();

        if (!divisionsEnabled) {
            _.pull(self.visibleColumnIds, 'divisions');
        }

        // ====================================================================
        // Filtering scheduled callbacks
        // ====================================================================

        self.queue = ko.observable();
        self.searchQueues = function (searchTerm) {
            return divisionsEnabled ?
                queuesService.getQueuesBrief({ name: searchTerm }, { returnModels: false }).then(function (res) {
                    return res.entities;
                }) :
                queuesService.getQueues({ name: searchTerm }, { returnModels: false }).then(function (res) {
                    return res.entities;
                });
        };

        self.campaign = ko.observable();
        self.searchCampaigns = function (searchTerm) {
            var campaignsUrl = divisionsEnabled ? '/outbound/campaigns/divisionviews' : '/outbound/campaigns';
            return apiCore.v2.get(campaignsUrl, { name: searchTerm }).then(function (res) {
                return res.entities;
            });
        };

        self.division = ko.observable();
        self.searchDivisions = function (searchTerm) {
            return divisionService.getDivisionsPermitted('analytics:conversationDetail:view', { name: searchTerm });
        };

        // ====================================================================
        // Fetching scheduled callbacks
        // ====================================================================

        var getQueue = _.memoize(function (queueId) {
            return !divisionsEnabled ?
                queuesService.getQueue(queueId) :
                queuesService.getQueuesBrief({ id: queueId }).then(function (res) {
                    return _.head(res.entities);
                });
        });
        var getQueueName = function (scheduledCallbackRow) {
            return !scheduledCallbackRow.queueId() ? Promise.resolve('') :
                getQueue(scheduledCallbackRow.queueId()).then(function (queue) {
                    scheduledCallbackRow.queueName(ko.unwrap(queue.name));
                });
        };

        var getCampaign = _.memoize(function (campaignId) {
            return !divisionsEnabled ?
                apiCore.v2.get('/outbound/campaigns/' + campaignId) :
                apiCore.v2.get('/outbound/campaigns/divisionviews', { id: campaignId }).then(function (res) {
                    return _.head(res.entities);
                });
        });
        var getCampaignInfo = function (scheduledCallbackRow) {
            return !scheduledCallbackRow.campaignId() ? Promise.resolve('') :
                getCampaign(scheduledCallbackRow.campaignId()).then(function (campaign) {
                    if (campaign) {
                        scheduledCallbackRow.campaignName(campaign.name);
                    }
                });
        };

        var getDivision = _.memoize(function (divisionId) {
            return divisionService.getDivision(divisionId);
        });
        var getDivisionNames = function (scheduledCallbackRow) {
            return !scheduledCallbackRow.divisionIds() ? Promise.resolve('') :
                Promise.all(_.map(scheduledCallbackRow.divisionIds(), getDivision)).then(function (divisions) {
                    scheduledCallbackRow.divisionNames(ko.i18n.renderString('scheduledCallbacks.divisionNames', {
                        firstDivision: _.get(_.head(divisions), 'name'),
                        divisions: _(divisions).tail().map('name').value()
                    }));
                });
        };

        self.removeRow = function (conversationId) {
            self.rows.remove(function (row) {
                return row.analyticsConversation.conversationId() === conversationId;
            });
        };

        self.fetchScheduledCallbacks = function () {
            var queryOptions = {
                interval: last14Days(),
                order: 'desc',
                segmentFilters: getSegmentFilters(self.queue(), self.campaign()),
                conversationFilters: getConversationFilters(self.division()),
                paging: self.pageOptions(),
                orderBy: 'conversationStart'
            };

            return apiCore.v2.post('/analytics/conversations/details/query', queryOptions)
                .then(function (res) {
                    var rows = _.map(res.conversations, _.partial(self.subView, ScheduledCallbackRow, self));

                    var queuePromises = _.map(rows, getQueueName);
                    var campaignPromises = _.map(rows, getCampaignInfo);
                    var divisionPromises = _.map(rows, getDivisionNames);

                    return Promise.all(_.union(queuePromises, campaignPromises, divisionPromises))
                        .finally(function () {
                            self.rows(rows);
                        });
                })
                .finally(function () {
                    self.isLoading(false);
                });
        };

        self.subscribe(self.pageOptions, self.fetchScheduledCallbacks);
        self.fetchScheduledCallbacks();

        // ====================================================================
        // Refresh Handling
        // ====================================================================

        var SECONDS_TO_REFRESH = 10;
        var refreshCountdown = SECONDS_TO_REFRESH;
        self.autoRefreshInterval = undefined;

        self.refreshRows = function () {
            if (onlineService.isOnline() && (--refreshCountdown) <= 0) {
                refreshCountdown = SECONDS_TO_REFRESH;
                self.fetchScheduledCallbacks();
            }
        };

        self.startAutoRefresh = function () {
            // Create an interval timer if one does not already exist
            if (!self.autoRefreshInterval) {
                self.autoRefreshInterval = setInterval(self.refreshRows, 1000);
            }
        };

        self.stopAutoRefresh = function () {
            clearInterval(self.autoRefreshInterval);
            self.autoRefreshInterval = undefined;
        };

        // ====================================================================
        // Init grid
        // ====================================================================

        self.dataTable = {
            alternatePager: {
                view: self.pager,
                template: 'templates/widgets/pager'
            },
            columns: self.columns,
            loading: self.isLoading,
            onDataOptionsChange: self.fetchScheduledCallbacks,
            rows: self.rows,
            visibleColumnIds: self.visibleColumnIds
        };

        self.startAutoRefresh();
        onlineService.whenOnline(self.startAutoRefresh);
        onlineService.whenOffline(self.stopAutoRefresh);

        // ====================================================================
        // ViewModel functionality
        // ====================================================================

        self.onDispose = function () {
            self.stopAutoRefresh();
            onlineService.unsubscribe(self.startAutoRefresh, self.stopAutoRefresh);
        };
    });
});

